import React, { useState, useRef, useEffect, useCallback, useReducer, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
    Video, FileText, Music, Upload, Link, Sparkles, CheckCircle, AlertCircle,
    ChevronLeft, ChevronRight, X, Loader2, ExternalLink, Trash2, Square,
    Mic, Clock, User, File, RefreshCw, Play, Pause, Volume2
} from 'lucide-react';
import vaultService from '../../../services/vaultService';
import transcriptionService from '../../../services/transcriptionService';
import TranscriptionResultModal from './TranscriptionResultModal';
import TranscriptionProgressOverlay from './TranscriptionProgressOverlay';

// Local storage keys for persisting state
const UPLOAD_STORAGE_KEY = 'podguestly_video_upload_state';
const TRANSCRIPT_STORAGE_KEY = 'podguestly_transcript_state';

// Transcription state reducer for complex state management
const transcriptionReducer = (state, action) => {
    switch (action.type) {
        case 'START_TRANSCRIPTION':
            return {
                ...state,
                isTranscribing: true,
                transcriptionProgress: 0,
                transcriptionError: null,
                transcriptionTaskId: action.payload?.taskId || null,
                transcriptionStage: 'initializing',
                transcriptionStartTime: Date.now(),
                transcriptionMessage: 'Starting transcription...'
            };
        case 'UPDATE_PROGRESS':
            return {
                ...state,
                transcriptionProgress: action.payload.progress ?? state.transcriptionProgress,
                transcriptionStage: action.payload.stage || state.transcriptionStage,
                transcriptionMessage: action.payload.message || state.transcriptionMessage
            };
        case 'TRANSCRIPTION_COMPLETE':
            return {
                ...state,
                isTranscribing: false,
                transcriptionProgress: 100,
                transcript: action.payload,
                transcriptionTaskId: null,
                transcriptionStage: 'complete',
                showResultModal: true
            };
        case 'TRANSCRIPTION_ERROR':
            return {
                ...state,
                isTranscribing: false,
                transcriptionProgress: 0,
                transcriptionError: action.payload,
                transcriptionTaskId: null,
                transcriptionStage: 'error'
            };
        case 'CANCEL_TRANSCRIPTION':
            return {
                ...state,
                isTranscribing: false,
                transcriptionProgress: 0,
                isCancellingTranscription: false,
                transcriptionTaskId: null,
                transcriptionStage: 'cancelled'
            };
        case 'START_CANCELLING':
            return {
                ...state,
                isCancellingTranscription: true
            };
        case 'SET_TRANSCRIPT':
            return {
                ...state,
                transcript: action.payload
            };
        case 'REMOVE_TRANSCRIPT':
            return {
                ...state,
                transcript: null,
                transcriptionError: null
            };
        case 'START_UPLOAD':
            return {
                ...state,
                isUploadingTranscript: true,
                uploadTranscriptProgress: 0,
                uploadTranscriptError: null
            };
        case 'UPDATE_UPLOAD_PROGRESS':
            return {
                ...state,
                uploadTranscriptProgress: action.payload
            };
        case 'UPLOAD_COMPLETE':
            return {
                ...state,
                isUploadingTranscript: false,
                uploadTranscriptProgress: 100,
                transcript: action.payload
            };
        case 'UPLOAD_ERROR':
            return {
                ...state,
                isUploadingTranscript: false,
                uploadTranscriptProgress: 0,
                uploadTranscriptError: action.payload
            };
        case 'SHOW_RESULT_MODAL':
            return {
                ...state,
                showResultModal: true
            };
        case 'HIDE_RESULT_MODAL':
            return {
                ...state,
                showResultModal: false
            };
        default:
            return state;
    }
};

// Initial transcription state
const initialTranscriptionState = {
    isTranscribing: false,
    transcriptionProgress: 0,
    transcriptionError: null,
    transcriptionTaskId: null,
    isCancellingTranscription: false,
    transcript: null,
    isUploadingTranscript: false,
    uploadTranscriptProgress: 0,
    uploadTranscriptError: null,
    transcriptionStage: 'idle',
    transcriptionStartTime: null,
    transcriptionMessage: '',
    showResultModal: false
};

/**
 * RepurposeSidebar - Collapsible left sidebar for content source
 * 
 * Features:
 * - Video upload with 5GB limit and progress tracking
 * - Upload cancellation with AbortController
 * - Remove uploaded video functionality
 * - YouTube URL input with validation
 * - Transcription via AssemblyAI with progress tracking
 * - Direct transcript upload (txt, json, srt, vtt)
 * - Firebase Cloud Storage integration
 * - Vault integration for transcript indexing
 * - State persistence across page refreshes
 * - Comprehensive error handling with toast notifications
 */
const RepurposeSidebar = ({
    episode,
    user,
    db,
    hasVideo = false,
    hasTranscript = false,
    hasAudio = false,
    onVideoUpload,
    onTranscriptGenerate,
    onAudioUpload,
    onUploadComplete,
    isCollapsed = false,
    onToggleCollapse,
    onTranscriptUpdate
}) => {
    // Video upload states
    const [isUploading, setIsUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [uploadError, setUploadError] = useState(null);
    const [uploadedAsset, setUploadedAsset] = useState(null);
    const [isCancelling, setIsCancelling] = useState(false);

    // YouTube URL states
    const [showUrlInput, setShowUrlInput] = useState(false);
    const [youtubeUrl, setYoutubeUrl] = useState('');
    const [urlError, setUrlError] = useState(null);
    const [isSavingUrl, setIsSavingUrl] = useState(false);
    const [savedUrl, setSavedUrl] = useState(null);

    // Audio upload states
    const [isUploadingAudio, setIsUploadingAudio] = useState(false);
    const [audioUploadProgress, setAudioUploadProgress] = useState(0);
    const [audioUploadError, setAudioUploadError] = useState(null);
    const [uploadedAudio, setUploadedAudio] = useState(null);
    const [isCancellingAudio, setIsCancellingAudio] = useState(false);
    const [audioPreviewUrl, setAudioPreviewUrl] = useState(null);
    const [isPlayingAudio, setIsPlayingAudio] = useState(false);
    const [audioDuration, setAudioDuration] = useState(0);
    const [audioCurrentTime, setAudioCurrentTime] = useState(0);

    // Source selection modal state for transcription
    const [showSourceSelectionModal, setShowSourceSelectionModal] = useState(false);

    // Transcription state using useReducer for complex state
    const [transcriptionState, dispatch] = useReducer(transcriptionReducer, initialTranscriptionState);

    // Toast notification state
    const [toast, setToast] = useState(null);

    // File input refs
    const fileInputRef = useRef(null);
    const transcriptInputRef = useRef(null);
    const audioInputRef = useRef(null);
    const audioPlayerRef = useRef(null);

    // AbortController refs for cancellation
    const abortControllerRef = useRef(null);
    const transcriptionAbortControllerRef = useRef(null);
    const audioAbortControllerRef = useRef(null);

    // Maximum file sizes
    const MAX_FILE_SIZE_GB = 5;
    const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_GB * 1024 * 1024 * 1024;
    const MAX_TRANSCRIPT_SIZE_MB = 10;
    const MAX_AUDIO_SIZE_MB = 1024; // 1GB max for audio files
    const MAX_AUDIO_SIZE_BYTES = MAX_AUDIO_SIZE_MB * 1024 * 1024;

    // Supported audio formats
    const SUPPORTED_AUDIO_FORMATS = ['audio/mpeg', 'audio/mp3', 'audio/wav', 'audio/x-wav', 'audio/m4a', 'audio/x-m4a', 'audio/mp4', 'audio/aac', 'audio/ogg', 'audio/flac'];
    const SUPPORTED_AUDIO_EXTENSIONS = ['.mp3', '.wav', '.m4a', '.aac', '.ogg', '.flac'];

    // Memoized computed values
    const canTranscribe = useMemo(() => {
        const hasVideoSource = !!(
            uploadedAsset?.downloadUrl ||
            uploadedAsset?.url ||
            savedUrl?.url ||
            hasVideo
        );
        const hasAudioSource = !!(
            uploadedAudio?.downloadUrl ||
            uploadedAudio?.url ||
            uploadedAudio?.storagePath ||
            hasAudio
        );
        console.log('[canTranscribe] Check:', {
            hasUploadedAsset: !!uploadedAsset,
            hasDownloadUrl: !!uploadedAsset?.downloadUrl,
            hasUrl: !!uploadedAsset?.url,
            hasSavedUrl: !!savedUrl?.url,
            hasVideoProp: hasVideo,
            hasVideoSource,
            hasAudioSource,
            hasUploadedAudio: !!uploadedAudio,
            hasAudioProp: hasAudio,
            isTranscribing: transcriptionState.isTranscribing,
            result: (hasVideoSource || hasAudioSource) && !transcriptionState.isTranscribing
        });
        return (hasVideoSource || hasAudioSource) && !transcriptionState.isTranscribing;
    }, [uploadedAsset, savedUrl, hasVideo, uploadedAudio, hasAudio, transcriptionState.isTranscribing]);

    // Check if both video and audio sources are available
    const hasBothSources = useMemo(() => {
        const hasVideoSource = !!(
            uploadedAsset?.downloadUrl ||
            uploadedAsset?.url ||
            savedUrl?.url ||
            hasVideo
        );
        const hasAudioSource = !!(
            uploadedAudio?.downloadUrl ||
            uploadedAudio?.url ||
            uploadedAudio?.storagePath ||
            hasAudio
        );
        return hasVideoSource && hasAudioSource;
    }, [uploadedAsset, savedUrl, hasVideo, uploadedAudio, hasAudio]);

    const hasExistingTranscript = useMemo(() => {
        return hasTranscript || !!transcriptionState.transcript;
    }, [hasTranscript, transcriptionState.transcript]);

    /**
     * Show toast notification
     */
    const showToast = useCallback((message, type = 'info') => {
        setToast({ message, type, id: Date.now() });
        setTimeout(() => setToast(null), 5000);
    }, []);

    /**
     * Load persisted states from localStorage on mount
     */
    useEffect(() => {
        try {
            // Load video upload state
            const storedUploadState = localStorage.getItem(UPLOAD_STORAGE_KEY);
            if (storedUploadState) {
                const parsedState = JSON.parse(storedUploadState);
                if (parsedState.episodeId === episode?.id) {
                    if (parsedState.uploadedAsset) {
                        setUploadedAsset(parsedState.uploadedAsset);
                    }
                    if (parsedState.savedUrl) {
                        setSavedUrl(parsedState.savedUrl);
                    }
                }
            }

            // Load transcript state
            const storedTranscriptState = localStorage.getItem(TRANSCRIPT_STORAGE_KEY);
            if (storedTranscriptState) {
                const parsedTranscript = JSON.parse(storedTranscriptState);
                if (parsedTranscript.episodeId === episode?.id && parsedTranscript.transcript) {
                    dispatch({ type: 'SET_TRANSCRIPT', payload: parsedTranscript.transcript });
                }
            }
        } catch (error) {
            console.error('Failed to load persisted state:', error);
        }
    }, [episode?.id]);

    /**
     * Persist upload state to localStorage whenever it changes
     */
    const persistUploadState = useCallback((asset, url) => {
        try {
            const stateToStore = {
                episodeId: episode?.id,
                uploadedAsset: asset,
                savedUrl: url,
                timestamp: Date.now()
            };
            localStorage.setItem(UPLOAD_STORAGE_KEY, JSON.stringify(stateToStore));
        } catch (error) {
            console.error('Failed to persist upload state:', error);
        }
    }, [episode?.id]);

    // Persist state when uploadedAsset or savedUrl changes
    useEffect(() => {
        if (uploadedAsset || savedUrl) {
            persistUploadState(uploadedAsset, savedUrl);
        }
    }, [uploadedAsset, savedUrl, persistUploadState]);

    /**
     * Persist transcript state to localStorage
     */
    const persistTranscriptState = useCallback((transcript) => {
        try {
            const stateToStore = {
                episodeId: episode?.id,
                transcript,
                timestamp: Date.now()
            };
            localStorage.setItem(TRANSCRIPT_STORAGE_KEY, JSON.stringify(stateToStore));
        } catch (error) {
            console.error('Failed to persist transcript state:', error);
        }
    }, [episode?.id]);

    // Persist transcript when it changes
    useEffect(() => {
        if (transcriptionState.transcript) {
            persistTranscriptState(transcriptionState.transcript);
        }
    }, [transcriptionState.transcript, persistTranscriptState]);

    /**
     * Clear persisted states from localStorage
     */
    const clearPersistedState = useCallback(() => {
        try {
            localStorage.removeItem(UPLOAD_STORAGE_KEY);
            localStorage.removeItem(TRANSCRIPT_STORAGE_KEY);
        } catch (error) {
            console.error('Failed to clear persisted state:', error);
        }
    }, []);

    // Content source status - memoized to prevent unnecessary recalculations
    const sources = useMemo(() => [
        { id: 'video', icon: Video, label: 'Video', hasContent: hasVideo || !!uploadedAsset || !!savedUrl },
        { id: 'transcript', icon: FileText, label: 'Transcript', hasContent: hasExistingTranscript },
        { id: 'audio', icon: Music, label: 'Audio', hasContent: hasAudio || !!uploadedAudio }
    ], [hasVideo, uploadedAsset, savedUrl, hasExistingTranscript, hasAudio, uploadedAudio]);

    const readyCount = useMemo(() => sources.filter(s => s.hasContent).length, [sources]);
    const isReady = readyCount > 0;

    /**
     * Validate YouTube URL format
     */
    const validateYouTubeUrl = (url) => {
        if (!url || typeof url !== 'string') {
            return { valid: false, error: 'Please enter a URL' };
        }

        const trimmedUrl = url.trim();
        const youtubePatterns = [
            /^(https?:\/\/)?(www\.)?youtube\.com\/watch\?v=[\w-]+/,
            /^(https?:\/\/)?(www\.)?youtu\.be\/[\w-]+/,
            /^(https?:\/\/)?(www\.)?youtube\.com\/embed\/[\w-]+/,
            /^(https?:\/\/)?(m\.)?youtube\.com\/watch\?v=[\w-]+/
        ];

        const isValid = youtubePatterns.some(pattern => pattern.test(trimmedUrl));

        if (!isValid) {
            return {
                valid: false,
                error: 'Please enter a valid YouTube URL (e.g., youtube.com/watch?v=...)'
            };
        }

        let videoId = null;
        const watchMatch = trimmedUrl.match(/[?&]v=([\w-]+)/);
        const shortMatch = trimmedUrl.match(/youtu\.be\/([\w-]+)/);
        const embedMatch = trimmedUrl.match(/embed\/([\w-]+)/);

        if (watchMatch) videoId = watchMatch[1];
        else if (shortMatch) videoId = shortMatch[1];
        else if (embedMatch) videoId = embedMatch[1];

        return { valid: true, videoId, url: trimmedUrl };
    };

    /**
     * Handle video file upload with cancellation support
     */
    const handleFileSelect = async (event) => {
        const file = event.target.files?.[0];
        if (!file) return;

        setUploadError(null);
        setUploadedAsset(null);
        setIsCancelling(false);

        // Client-side validation
        if (file.size > MAX_FILE_SIZE_BYTES) {
            setUploadError(`File size exceeds ${MAX_FILE_SIZE_GB}GB limit. Your file: ${vaultService.formatFileSize(file.size)}`);
            return;
        }

        const validation = vaultService.validateFile(file, MAX_FILE_SIZE_GB * 1024);
        if (!validation.valid) {
            setUploadError(validation.error);
            return;
        }

        // Create new AbortController for this upload
        abortControllerRef.current = new AbortController();
        const signal = abortControllerRef.current.signal;

        setIsUploading(true);
        setUploadProgress(0);

        try {
            const result = await vaultService.uploadFile(file, {
                episodeId: episode?.id,
                tags: ['video', 'source'],
                description: `Source video for episode: ${episode?.episodeTitle || episode?.title || 'Untitled'}`,
                onProgress: (stats) => {
                    // stats is an object with progress property
                    const progressValue = typeof stats === 'object' ? stats.progress : stats;
                    setUploadProgress(progressValue);
                },
                signal // Pass abort signal
            });

            if (result.success) {
                setUploadedAsset(result.asset);
                if (onUploadComplete) onUploadComplete(result.asset);
                if (onVideoUpload) onVideoUpload(result.asset);
            } else {
                setUploadError(result.error || 'Upload failed');
            }
        } catch (error) {
            // Handle cancellation gracefully
            if (error.message === 'Upload cancelled') {
                setUploadError('Upload was cancelled');
            } else {
                setUploadError(error.message || 'Upload failed. Please try again.');
            }
        } finally {
            setIsUploading(false);
            setIsCancelling(false);
            abortControllerRef.current = null;
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    /**
     * Cancel ongoing upload
     */
    const handleCancelUpload = () => {
        if (abortControllerRef.current) {
            setIsCancelling(true);
            abortControllerRef.current.abort();
            // Reset progress immediately for visual feedback
            setUploadProgress(0);
        }
    };

    /**
     * Remove uploaded video and reset state
     */
    const handleRemoveUpload = async () => {
        if (uploadedAsset?.id) {
            try {
                // Optionally delete from server
                await vaultService.deleteAsset(uploadedAsset.id);
            } catch (error) {
                console.error('Failed to delete asset from server:', error);
                // Continue with local cleanup even if server delete fails
            }
        }

        // Reset all upload state
        setUploadedAsset(null);
        setUploadProgress(0);
        setUploadError(null);
        clearPersistedState();

        // Reset file input
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    /**
     * Remove saved YouTube URL and reset state
     */
    const handleRemoveUrl = () => {
        setSavedUrl(null);
        setYoutubeUrl('');
        setUrlError(null);
        clearPersistedState();
    };

    // ==================== AUDIO UPLOAD FUNCTIONS ====================

    /**
     * Validate audio file format and size
     */
    const validateAudioFile = (file) => {
        if (!file) {
            return { valid: false, error: 'No file selected' };
        }

        // Check file size
        if (file.size > MAX_AUDIO_SIZE_BYTES) {
            return {
                valid: false,
                error: `File size exceeds ${MAX_AUDIO_SIZE_MB}MB limit. Your file: ${vaultService.formatFileSize(file.size)}`
            };
        }

        // Check file type by MIME type
        const mimeType = file.type.toLowerCase();
        const isValidMimeType = SUPPORTED_AUDIO_FORMATS.includes(mimeType);

        // Check file extension as fallback
        const fileName = file.name.toLowerCase();
        const hasValidExtension = SUPPORTED_AUDIO_EXTENSIONS.some(ext => fileName.endsWith(ext));

        if (!isValidMimeType && !hasValidExtension) {
            return {
                valid: false,
                error: `Unsupported audio format. Supported formats: ${SUPPORTED_AUDIO_EXTENSIONS.join(', ')}`
            };
        }

        return { valid: true };
    };

    /**
     * Handle audio file selection and immediate upload (inline pattern like video)
     */
    const handleAudioFileSelect = async (event) => {
        const file = event.target.files?.[0];
        if (!file) return;

        setAudioUploadError(null);
        setIsCancellingAudio(false);

        // Validate the file
        const validation = validateAudioFile(file);
        if (!validation.valid) {
            setAudioUploadError(validation.error);
            return;
        }

        // Create preview URL for audio player
        if (audioPreviewUrl) {
            URL.revokeObjectURL(audioPreviewUrl);
        }
        const previewUrl = URL.createObjectURL(file);
        setAudioPreviewUrl(previewUrl);

        // Create new AbortController for this upload
        audioAbortControllerRef.current = new AbortController();
        const signal = audioAbortControllerRef.current.signal;

        setIsUploadingAudio(true);
        setAudioUploadProgress(0);

        try {
            const result = await vaultService.uploadFile(file, {
                episodeId: episode?.id,
                tags: ['audio', 'source'],
                description: `Source audio for episode: ${episode?.episodeTitle || episode?.title || 'Untitled'}`,
                onProgress: (stats) => {
                    // stats is an object with progress property
                    const progressValue = typeof stats === 'object' ? stats.progress : stats;
                    setAudioUploadProgress(progressValue);
                },
                signal
            });

            if (result.success) {
                setUploadedAudio(result.asset);
                showToast('Audio uploaded successfully!', 'success');

                if (onAudioUpload) {
                    onAudioUpload(result.asset);
                }
                if (onUploadComplete) {
                    onUploadComplete(result.asset);
                }
            } else {
                setAudioUploadError(result.error || 'Upload failed');
            }
        } catch (error) {
            if (error.message === 'Upload cancelled') {
                setAudioUploadError('Upload was cancelled');
            } else {
                setAudioUploadError(error.message || 'Upload failed. Please try again.');
            }
        } finally {
            setIsUploadingAudio(false);
            setIsCancellingAudio(false);
            audioAbortControllerRef.current = null;
            if (audioInputRef.current) audioInputRef.current.value = '';
        }
    };

    /**
     * Cancel ongoing audio upload
     */
    const handleCancelAudioUpload = () => {
        if (audioAbortControllerRef.current) {
            setIsCancellingAudio(true);
            audioAbortControllerRef.current.abort();
            setAudioUploadProgress(0);
        }
    };

    /**
     * Handle audio player play/pause
     */
    const handleAudioPlayPause = () => {
        if (!audioPlayerRef.current) return;

        if (isPlayingAudio) {
            audioPlayerRef.current.pause();
        } else {
            audioPlayerRef.current.play();
        }
        setIsPlayingAudio(!isPlayingAudio);
    };

    /**
     * Handle audio time update
     */
    const handleAudioTimeUpdate = () => {
        if (!audioPlayerRef.current) return;
        setAudioCurrentTime(audioPlayerRef.current.currentTime);
    };

    /**
     * Handle audio metadata loaded (duration)
     */
    const handleAudioLoadedMetadata = () => {
        if (!audioPlayerRef.current) return;
        setAudioDuration(audioPlayerRef.current.duration);
    };

    /**
     * Handle audio seek
     */
    const handleAudioSeek = (e) => {
        if (!audioPlayerRef.current) return;
        const seekTime = (e.target.value / 100) * audioDuration;
        audioPlayerRef.current.currentTime = seekTime;
        setAudioCurrentTime(seekTime);
    };

    /**
     * Format time in MM:SS format
     */
    const formatAudioTime = (seconds) => {
        if (isNaN(seconds) || !isFinite(seconds)) return '0:00';
        const mins = Math.floor(seconds / 60);
        const secs = Math.floor(seconds % 60);
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    /**
     * Remove uploaded audio
     */
    const handleRemoveAudio = async () => {
        // Stop audio playback
        if (audioPlayerRef.current) {
            audioPlayerRef.current.pause();
            audioPlayerRef.current.currentTime = 0;
        }
        setIsPlayingAudio(false);

        if (uploadedAudio?.id) {
            try {
                await vaultService.deleteAsset(uploadedAudio.id);
            } catch (error) {
                console.error('Failed to delete audio from server:', error);
            }
        }

        setUploadedAudio(null);
        if (audioPreviewUrl) {
            URL.revokeObjectURL(audioPreviewUrl);
        }
        setAudioPreviewUrl(null);
        setAudioUploadProgress(0);
        setAudioUploadError(null);
        setAudioDuration(0);
        setAudioCurrentTime(0);
        clearPersistedState();
        showToast('Audio removed', 'info');
    };

    // ==================== TRANSCRIPTION FUNCTIONS ====================

    /**
     * Determine which source to use for transcription
     * Shows selection modal if both video and audio are available
     */
    const handleStartTranscription = useCallback(async () => {
        if (!canTranscribe) {
            showToast('Please upload a video, audio, or add a YouTube URL first', 'error');
            return;
        }

        // Check if both sources are available
        if (hasBothSources) {
            // Show modal to let user select source
            setShowSourceSelectionModal(true);
            return;
        }

        // Determine source automatically
        const hasVideoSource = !!(
            uploadedAsset?.downloadUrl ||
            uploadedAsset?.url ||
            savedUrl?.url ||
            hasVideo
        );
        const hasAudioSource = !!(
            uploadedAudio?.downloadUrl ||
            uploadedAudio?.url ||
            uploadedAudio?.storagePath ||
            hasAudio
        );

        if (hasVideoSource) {
            await executeTranscription('video');
        } else if (hasAudioSource) {
            await executeTranscription('audio');
        }
    }, [canTranscribe, hasBothSources, uploadedAsset, savedUrl, hasVideo, uploadedAudio, hasAudio]);

    /**
     * Execute transcription from selected source
     * @param {string} sourceType - 'video' or 'audio'
     */
    const executeTranscription = useCallback(async (sourceType) => {
        dispatch({ type: 'START_TRANSCRIPTION' });
        transcriptionAbortControllerRef.current = new AbortController();
        const signal = transcriptionAbortControllerRef.current.signal;

        try {
            let transcriptResult;
            let actualSourceType = sourceType;

            if (sourceType === 'video' && savedUrl?.url) {
                // Transcribe from YouTube URL
                dispatch({
                    type: 'UPDATE_PROGRESS',
                    payload: { progress: 5, stage: 'downloading', message: 'Downloading from YouTube...' }
                });

                const result = await transcriptionService.transcribeFromUrl(savedUrl.url, {
                    episodeId: episode?.id,
                    onProgress: (progress) => {
                        dispatch({
                            type: 'UPDATE_PROGRESS',
                            payload: {
                                progress: progress.progress || 10,
                                stage: progress.stage || 'extracting',
                                message: progress.message || 'Processing...'
                            }
                        });
                    }
                });

                transcriptResult = await transcriptionService.pollForCompletion(result.transcriptId, {
                    onProgress: (progress) => {
                        dispatch({
                            type: 'UPDATE_PROGRESS',
                            payload: {
                                progress: progress.progress || 50,
                                stage: 'transcribing',
                                message: 'Transcribing audio...'
                            }
                        });
                    },
                    signal
                });
                actualSourceType = 'youtube';

            } else if (sourceType === 'video' && uploadedAsset?.storagePath) {
                // Transcribe from uploaded video in Firebase Storage
                const storagePath = uploadedAsset.storagePath;
                const fileName = uploadedAsset.fileName || uploadedAsset.storageName || 'video.mp4';

                dispatch({
                    type: 'UPDATE_PROGRESS',
                    payload: { progress: 5, stage: 'extracting', message: 'Extracting audio from video...' }
                });

                const result = await transcriptionService.transcribeFromStorageUrl(storagePath, fileName, {
                    episodeId: episode?.id,
                    onProgress: (progress) => {
                        dispatch({
                            type: 'UPDATE_PROGRESS',
                            payload: {
                                progress: progress.progress || 10,
                                stage: progress.stage || 'extracting',
                                message: progress.message || 'Processing...'
                            }
                        });
                    }
                });

                transcriptResult = await transcriptionService.pollForCompletion(result.transcriptId, {
                    onProgress: (progress) => {
                        dispatch({
                            type: 'UPDATE_PROGRESS',
                            payload: {
                                progress: progress.progress || 50,
                                stage: 'transcribing',
                                message: 'Transcribing audio...'
                            }
                        });
                    },
                    signal
                });
                actualSourceType = 'video';

            } else if (sourceType === 'audio' && uploadedAudio?.storagePath) {
                // Transcribe from uploaded audio in Firebase Storage
                const storagePath = uploadedAudio.storagePath;
                const fileName = uploadedAudio.fileName || uploadedAudio.storageName || 'audio.mp3';

                dispatch({
                    type: 'UPDATE_PROGRESS',
                    payload: { progress: 5, stage: 'processing', message: 'Processing audio file...' }
                });

                const result = await transcriptionService.transcribeFromStorageUrl(storagePath, fileName, {
                    episodeId: episode?.id,
                    onProgress: (progress) => {
                        dispatch({
                            type: 'UPDATE_PROGRESS',
                            payload: {
                                progress: progress.progress || 10,
                                stage: progress.stage || 'processing',
                                message: progress.message || 'Processing audio...'
                            }
                        });
                    }
                });

                transcriptResult = await transcriptionService.pollForCompletion(result.transcriptId, {
                    onProgress: (progress) => {
                        dispatch({
                            type: 'UPDATE_PROGRESS',
                            payload: {
                                progress: progress.progress || 50,
                                stage: 'transcribing',
                                message: 'Transcribing audio...'
                            }
                        });
                    },
                    signal
                });
                actualSourceType = 'audio';

            } else {
                throw new Error('No valid source available for transcription');
            }

            // Process the completed transcript
            const transcriptData = {
                text: transcriptResult.text,
                utterances: transcriptResult.utterances || [],
                segments: transcriptResult.segments || [],
                speakers: transcriptResult.speakers || [],
                words: transcriptResult.words || [],
                duration: transcriptResult.audio_duration || transcriptResult.duration || 0,
                language: transcriptResult.language || 'en',
                confidence: transcriptResult.confidence,
                sourceType: actualSourceType,
                sourceUrl: sourceType === 'audio'
                    ? (uploadedAudio?.downloadUrl || uploadedAudio?.url)
                    : (savedUrl?.url || uploadedAsset?.downloadUrl || uploadedAsset?.url),
                title: transcriptResult.metadata?.title || episode?.title || 'Transcript',
                model: 'AssemblyAI Universal-2',
                id: transcriptResult.id,
                createdAt: new Date(),
                wordCount: transcriptResult.text?.split(/\s+/).filter(Boolean).length || 0
            };

            // Save to Firebase transcripts collection
            const savedTranscript = await transcriptionService.saveTranscript(transcriptData, {
                episodeId: episode?.id,
                db
            });

            dispatch({ type: 'TRANSCRIPTION_COMPLETE', payload: savedTranscript });
            showToast('Transcription completed successfully!', 'success');

            if (onTranscriptUpdate) {
                onTranscriptUpdate(savedTranscript);
            }

        } catch (error) {
            if (error.message === 'Polling cancelled' || error.message === 'Transcription cancelled') {
                dispatch({ type: 'CANCEL_TRANSCRIPTION' });
                showToast('Transcription was cancelled', 'info');
            } else {
                dispatch({ type: 'TRANSCRIPTION_ERROR', payload: error.message });
                showToast(`Transcription failed: ${error.message}`, 'error');
            }
        } finally {
            transcriptionAbortControllerRef.current = null;
        }
    }, [savedUrl, uploadedAsset, uploadedAudio, episode?.id, db, showToast, onTranscriptUpdate, episode?.title]);

    /**
     * Handle source selection from modal
     * @param {string} sourceType - 'video' or 'audio'
     */
    const handleSourceSelection = useCallback((sourceType) => {
        setShowSourceSelectionModal(false);
        executeTranscription(sourceType);
    }, [executeTranscription]);

    /**
     * Cancel ongoing transcription
     */
    const handleCancelTranscription = useCallback(() => {
        if (transcriptionAbortControllerRef.current) {
            dispatch({ type: 'START_CANCELLING' });
            transcriptionAbortControllerRef.current.abort();
        }
    }, []);

    /**
     * Remove existing transcript
     */
    const handleRemoveTranscript = useCallback(async () => {
        if (transcriptionState.transcript?.id) {
            try {
                // Delete from vault if indexed
                await vaultService.deleteAsset(transcriptionState.transcript.id);
            } catch (error) {
                console.error('Failed to delete transcript from vault:', error);
            }
        }

        dispatch({ type: 'REMOVE_TRANSCRIPT' });
        clearPersistedState();
        showToast('Transcript removed', 'info');

        if (onTranscriptUpdate) {
            onTranscriptUpdate(null);
        }
    }, [transcriptionState.transcript, clearPersistedState, showToast, onTranscriptUpdate]);

    /**
     * Handle direct transcript file upload (txt, json, srt, vtt)
     */
    const handleTranscriptFileSelect = useCallback(async (event) => {
        const file = event.target.files?.[0];
        if (!file) return;

        // Validate file size
        if (file.size > MAX_TRANSCRIPT_SIZE_MB * 1024 * 1024) {
            dispatch({
                type: 'UPLOAD_ERROR',
                payload: `File size exceeds ${MAX_TRANSCRIPT_SIZE_MB}MB limit`
            });
            showToast(`File size exceeds ${MAX_TRANSCRIPT_SIZE_MB}MB limit`, 'error');
            return;
        }

        dispatch({ type: 'START_UPLOAD' });

        try {
            // Read file content
            const content = await file.text();

            // Parse based on file type
            const parsedTranscript = transcriptionService.parseTranscriptFile(content, file.name);

            // Save to Firebase
            const transcriptData = {
                ...parsedTranscript,
                sourceType: 'upload',
                fileName: file.name,
                model: 'manual'
            };

            const savedTranscript = await transcriptionService.saveTranscript(transcriptData, {
                episodeId: episode?.id,
                db
            });

            dispatch({ type: 'UPLOAD_COMPLETE', payload: savedTranscript });
            showToast('Transcript uploaded successfully!', 'success');

            if (onTranscriptUpdate) {
                onTranscriptUpdate(savedTranscript);
            }
        } catch (error) {
            dispatch({ type: 'UPLOAD_ERROR', payload: error.message });
            showToast(`Failed to parse transcript: ${error.message}`, 'error');
        } finally {
            if (transcriptInputRef.current) {
                transcriptInputRef.current.value = '';
            }
        }
    }, [episode?.id, db, showToast, onTranscriptUpdate]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
            if (transcriptionAbortControllerRef.current) {
                transcriptionAbortControllerRef.current.abort();
            }
            if (audioAbortControllerRef.current) {
                audioAbortControllerRef.current.abort();
            }
        };
    }, []);

    /**
     * Handle YouTube URL submission
     */
    const handleUrlSubmit = async () => {
        const validation = validateYouTubeUrl(youtubeUrl);

        if (!validation.valid) {
            setUrlError(validation.error);
            return;
        }

        setUrlError(null);
        setIsSavingUrl(true);

        try {
            const token = await user?.getIdToken?.();

            const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5001'}/api/vault/youtube-source`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    url: validation.url,
                    videoId: validation.videoId,
                    episodeId: episode?.id,
                    episodeTitle: episode?.episodeTitle || episode?.title
                })
            });

            const result = await response.json();

            if (!response.ok) {
                throw new Error(result.error || 'Failed to save YouTube URL');
            }

            setSavedUrl({
                url: validation.url,
                videoId: validation.videoId,
                asset: result.asset
            });

            if (onUploadComplete) onUploadComplete(result.asset);

            setShowUrlInput(false);
            setYoutubeUrl('');

        } catch (error) {
            setUrlError(error.message || 'Failed to save YouTube URL');
        } finally {
            setIsSavingUrl(false);
        }
    };

    return (
        <div className="h-full flex flex-col border-r-2 border-slate-100 bg-white/50 backdrop-blur-sm relative">
            {/* Hidden file inputs */}
            <input
                ref={fileInputRef}
                type="file"
                accept="video/*"
                onChange={handleFileSelect}
                className="hidden"
            />
            <input
                ref={transcriptInputRef}
                type="file"
                accept=".txt,.json,.srt,.vtt"
                onChange={handleTranscriptFileSelect}
                className="hidden"
            />
            <input
                ref={audioInputRef}
                type="file"
                accept="audio/*,.mp3,.wav,.m4a,.aac,.ogg,.flac"
                onChange={handleAudioFileSelect}
                className="hidden"
            />

            {/* Toast Notification */}
            <AnimatePresence>
                {toast && (
                    <motion.div
                        initial={{ opacity: 0, y: -20, x: '-50%' }}
                        animate={{ opacity: 1, y: 0, x: '-50%' }}
                        exit={{ opacity: 0, y: -20, x: '-50%' }}
                        className={`absolute top-4 left-1/2 z-50 px-4 py-2 rounded-xl shadow-lg text-xs font-bold
                            ${toast.type === 'success' ? 'bg-green-500 text-white' :
                                toast.type === 'error' ? 'bg-red-500 text-white' :
                                    'bg-slate-700 text-white'}`}
                    >
                        {toast.message}
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Polished Floating Toggle Button */}
            <motion.button
                onClick={onToggleCollapse}
                whileTap={{ scale: 0.95 }}
                className="absolute -right-4 top-1/2 -translate-y-1/2 z-30 w-8 h-8 
                    bg-white rounded-full shadow-lg shadow-indigo-100/50 
                    border-2 border-indigo-100
                    flex items-center justify-center 
                    hover:border-indigo-300 hover:shadow-xl hover:shadow-indigo-200/50
                    hover:scale-105 active:scale-95
                    transition-all duration-200 origin-center"
            >
                <motion.div
                    animate={{ rotate: isCollapsed ? 0 : 180 }}
                    transition={{ duration: 0.2, ease: 'easeInOut' }}
                >
                    <ChevronRight size={16} className="text-indigo-500" />
                </motion.div>
            </motion.button>

            {/* Collapsed State - Icon Strip */}
            <AnimatePresence mode="wait">
                {isCollapsed && (
                    <motion.div
                        key="collapsed"
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -20 }}
                        transition={{ duration: 0.2 }}
                        className="w-16 min-w-16 max-w-16 h-full flex flex-col items-center py-6 space-y-5 overflow-hidden"
                    >
                        {/* Source Icon */}
                        <motion.div
                            whileHover={{ scale: 1.1 }}
                            className="w-11 h-11 rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shadow-lg shadow-indigo-200/50"
                        >
                            <Sparkles size={18} className="text-white" />
                        </motion.div>

                        <div className="w-6 h-0.5 bg-slate-200 rounded-full" />

                        {/* Ready Status Indicator */}
                        <motion.div
                            whileHover={{ scale: 1.1 }}
                            className={`w-11 h-11 rounded-2xl flex items-center justify-center border-2 transition-colors
                                ${isReady ? 'bg-green-50 border-green-200' : 'bg-slate-50 border-slate-200'}`}
                        >
                            <div className={`w-3 h-3 rounded-full ${isReady ? 'bg-green-500 shadow-sm shadow-green-300' : 'bg-slate-300'}`} />
                        </motion.div>

                        {/* Episode Icon */}
                        <motion.div
                            whileHover={{ scale: 1.1 }}
                            className="w-11 h-11 rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shadow-lg shadow-indigo-200/50 border-2 border-indigo-300"
                        >
                            <Video size={16} className="text-white" />
                        </motion.div>

                        {/* Source Icons */}
                        {sources.map((source) => {
                            const Icon = source.icon;
                            return (
                                <motion.div
                                    key={source.id}
                                    whileHover={{ scale: 1.1 }}
                                    className={`w-11 h-11 rounded-2xl flex items-center justify-center border-2 transition-colors
                                        ${source.hasContent ? 'bg-green-50 border-green-200' : 'bg-slate-50 border-slate-200'}`}
                                >
                                    <Icon size={16} className={source.hasContent ? 'text-green-600' : 'text-slate-400'} />
                                </motion.div>
                            );
                        })}
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Expanded State - Full Content */}
            <AnimatePresence mode="wait">
                {!isCollapsed && (
                    <motion.div
                        key="expanded"
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -20 }}
                        transition={{ duration: 0.2 }}
                        className="flex flex-col h-full min-w-[320px] max-w-[360px]"
                    >
                        {/* Header */}
                        <div className="p-5 border-b border-slate-100/50 flex justify-between items-center">
                            <h2 className="text-xs font-bold text-slate-400 uppercase tracking-widest flex items-center gap-2">
                                <Sparkles size={12} className="text-indigo-400" />
                                Content Source
                            </h2>
                            <div className={`text-xs font-bold ${isReady ? 'text-green-600' : 'text-slate-400'}`}>
                                {readyCount}/3 ready
                            </div>
                        </div>

                        {/* Episode Info Card */}
                        <div className="p-4">
                            <div className="bg-white rounded-[24px] p-1 shadow-xl shadow-indigo-100/50 border border-slate-100 group hover:-translate-y-1 transition-transform duration-300">
                                <div className="p-4 flex flex-col items-center text-center">
                                    <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-600 mb-3 flex items-center justify-center shadow-lg">
                                        <Video size={28} className="text-white" />
                                    </div>
                                    <h3 className="font-bold text-slate-900 font-['Outfit'] text-base leading-tight">
                                        {episode?.episodeTitle || episode?.title || 'Untitled Episode'}
                                    </h3>
                                    <p className="text-xs text-slate-500 font-medium mt-1">
                                        {episode?.founderName || 'Guest'}
                                    </p>
                                </div>

                                {/* Status Bar */}
                                <div className={`rounded-[20px] p-3 flex items-center justify-center gap-2 ${isReady ? 'bg-green-50' : 'bg-slate-50'}`}>
                                    {isReady ? (
                                        <>
                                            <CheckCircle size={14} className="text-green-500" />
                                            <span className="text-xs font-bold text-green-700">Ready to repurpose</span>
                                        </>
                                    ) : (
                                        <>
                                            <AlertCircle size={14} className="text-slate-400" />
                                            <span className="text-xs font-bold text-slate-500">Upload content to begin</span>
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Upload Progress with Cancel Button */}
                        <AnimatePresence>
                            {isUploading && (
                                <motion.div
                                    initial={{ opacity: 0, height: 0 }}
                                    animate={{ opacity: 1, height: 'auto' }}
                                    exit={{ opacity: 0, height: 0 }}
                                    className="px-4"
                                >
                                    <div className="bg-indigo-50 rounded-xl p-3 border border-indigo-100">
                                        <div className="flex items-center justify-between gap-2">
                                            <div className="flex items-center gap-2 flex-1 min-w-0">
                                                {isCancelling ? (
                                                    <X size={16} className="text-indigo-600" />
                                                ) : (
                                                    <Loader2 size={16} className="text-indigo-600 animate-spin" />
                                                )}
                                                <span className="text-xs font-bold text-indigo-900">
                                                    {isCancelling ? 'Cancelling...' : 'Uploading...'}
                                                </span>
                                            </div>
                                            {/* Cancel Button */}
                                            {!isCancelling && (
                                                <motion.button
                                                    whileHover={{ scale: 1.05 }}
                                                    whileTap={{ scale: 0.95 }}
                                                    onClick={handleCancelUpload}
                                                    className="flex items-center gap-1 px-2.5 py-1.5 bg-red-500 hover:bg-red-600 
                                                        text-white rounded-lg text-[10px] font-bold transition-colors flex-shrink-0 shadow-md"
                                                >
                                                    <Square size={10} />
                                                    Cancel
                                                </motion.button>
                                            )}
                                        </div>
                                        <div className="mt-2 h-1.5 bg-indigo-200 rounded-full overflow-hidden">
                                            <motion.div
                                                className="h-full bg-indigo-600 rounded-full"
                                                initial={{ width: 0 }}
                                                animate={{ width: `${uploadProgress}%` }}
                                            />
                                        </div>
                                        <p className="text-[10px] text-indigo-600 mt-1">{uploadProgress}%</p>
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>

                        {/* Success Messages with Remove Button */}
                        <AnimatePresence>
                            {(uploadedAsset || savedUrl) && !isUploading && (
                                <motion.div
                                    initial={{ opacity: 0, height: 0 }}
                                    animate={{ opacity: 1, height: 'auto' }}
                                    exit={{ opacity: 0, height: 0 }}
                                    className="px-4"
                                >
                                    <div className="bg-green-50 rounded-xl p-3 border border-green-200">
                                        <div className="flex items-center justify-between gap-2">
                                            <div className="flex items-center gap-2 flex-1 min-w-0">
                                                <CheckCircle size={14} className="text-green-600 flex-shrink-0" />
                                                <div className="min-w-0">
                                                    <span className="text-xs font-bold text-green-900 block">
                                                        {uploadedAsset ? 'Video uploaded!' : 'YouTube URL saved!'}
                                                    </span>
                                                    {uploadedAsset?.fileName && (
                                                        <span className="text-[10px] text-green-700 truncate block">
                                                            {uploadedAsset.fileName}
                                                        </span>
                                                    )}
                                                    {savedUrl && (
                                                        <a
                                                            href={savedUrl.url}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="text-[10px] text-green-700 hover:text-green-900 flex items-center gap-1 mt-0.5"
                                                        >
                                                            View on YouTube <ExternalLink size={10} />
                                                        </a>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>

                        {/* Error Messages */}
                        <AnimatePresence>
                            {(uploadError || urlError) && (
                                <motion.div
                                    initial={{ opacity: 0, height: 0 }}
                                    animate={{ opacity: 1, height: 'auto' }}
                                    exit={{ opacity: 0, height: 0 }}
                                    className="px-4"
                                >
                                    <div className="bg-red-50 rounded-xl p-3 border border-red-200">
                                        <div className="flex items-start gap-2">
                                            <AlertCircle size={14} className="text-red-600 mt-0.5" />
                                            <div className="flex-1">
                                                <span className="text-xs font-bold text-red-900">Upload failed</span>
                                                <p className="text-[10px] text-red-700 mt-0.5">{uploadError || urlError}</p>
                                            </div>
                                            <button
                                                onClick={() => { setUploadError(null); setUrlError(null); }}
                                                className="text-red-600 hover:text-red-800"
                                            >
                                                <X size={14} />
                                            </button>
                                        </div>
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>

                        {/* Content Sources */}
                        <div className="flex-1 overflow-y-auto p-4 space-y-3">
                            <h3 className="text-[10px] font-bold text-slate-400 uppercase tracking-widest pl-2">
                                Upload Sources
                            </h3>

                            {/* Video Source */}
                            <div className={`bg-white border p-4 rounded-xl shadow-sm hover:shadow-md transition-all
                                ${(uploadedAsset || savedUrl) ? 'border-green-200 ring-1 ring-green-100' : 'border-slate-100'}`}>
                                <div className="flex items-center justify-between mb-3">
                                    <div className="flex items-center gap-3">
                                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center
                                            ${(uploadedAsset || savedUrl) ? 'bg-green-100' : 'bg-indigo-100'}`}>
                                            <Video size={18} className={(uploadedAsset || savedUrl) ? 'text-green-600' : 'text-indigo-600'} />
                                        </div>
                                        <div>
                                            <h4 className="text-sm font-bold text-slate-800">Video</h4>
                                            <p className="text-[10px] text-slate-400">
                                                {(uploadedAsset || savedUrl) ? 'Added' : 'Upload or YouTube URL'}
                                            </p>
                                        </div>
                                    </div>
                                    {(uploadedAsset || savedUrl) && (
                                        <div className="w-5 h-5 bg-green-500 rounded-full flex items-center justify-center">
                                            <CheckCircle size={12} className="text-white" />
                                        </div>
                                    )}
                                </div>

                                {/* Action Buttons - Show Remove when content exists, Upload/URL when empty */}
                                {(uploadedAsset || savedUrl) ? (
                                    <button
                                        onClick={uploadedAsset ? handleRemoveUpload : handleRemoveUrl}
                                        className="w-full flex items-center justify-center gap-1.5 px-3 py-2 
                                            bg-red-500 hover:bg-red-600 text-white rounded-xl text-xs font-bold 
                                            shadow-md transition-all"
                                    >
                                        <Trash2 size={12} />
                                        <span>Remove</span>
                                    </button>
                                ) : (
                                    <div className="flex gap-2">
                                        <button
                                            onClick={() => fileInputRef.current?.click()}
                                            disabled={isUploading}
                                            className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 
                                                bg-indigo-600 text-white rounded-xl text-xs font-bold 
                                                hover:bg-indigo-700 shadow-md shadow-indigo-200 transition-all
                                                disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            <Upload size={12} />
                                            <span>Upload</span>
                                        </button>
                                        <button
                                            onClick={() => setShowUrlInput(true)}
                                            disabled={isUploading}
                                            className="flex items-center justify-center gap-1.5 px-3 py-2 
                                                bg-slate-100 text-slate-600 rounded-xl text-xs font-bold 
                                                hover:bg-slate-200 transition-all
                                                disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            <Link size={12} />
                                        </button>
                                    </div>
                                )}
                            </div>

                            {/* Transcript Source */}
                            <div className={`bg-white border p-4 rounded-xl shadow-sm hover:shadow-md transition-all
                                ${hasExistingTranscript ? 'border-green-200 ring-1 ring-green-100' : 'border-slate-100'}`}>
                                <div className="flex items-center justify-between mb-3">
                                    <div className="flex items-center gap-3">
                                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center
                                            ${hasExistingTranscript ? 'bg-green-100' : 'bg-slate-100'}`}>
                                            <FileText size={18} className={hasExistingTranscript ? 'text-green-600' : 'text-slate-500'} />
                                        </div>
                                        <div>
                                            <h4 className="text-sm font-bold text-slate-800">Transcript</h4>
                                            <p className="text-[10px] text-slate-400">
                                                {hasExistingTranscript ? `${transcriptionState.transcript?.wordCount || 0} words` : 'Generate or upload'}
                                            </p>
                                        </div>
                                    </div>
                                    {hasExistingTranscript && (
                                        <div className="w-5 h-5 bg-green-500 rounded-full flex items-center justify-center">
                                            <CheckCircle size={12} className="text-white" />
                                        </div>
                                    )}
                                </div>

                                {/* Transcript Actions */}
                                {hasExistingTranscript ? (
                                    <div className="space-y-2">
                                        {/* View Transcript Button */}
                                        <button
                                            onClick={() => dispatch({ type: 'SHOW_RESULT_MODAL' })}
                                            className="w-full flex items-center justify-center gap-1.5 px-3 py-2 
                                                bg-indigo-600 text-white rounded-xl text-xs font-bold 
                                                hover:bg-indigo-700 shadow-md shadow-indigo-200 transition-all"
                                        >
                                            <FileText size={12} />
                                            <span>View Transcript</span>
                                        </button>
                                        <button
                                            onClick={handleRemoveTranscript}
                                            className="w-full flex items-center justify-center gap-1.5 px-3 py-2 
                                                bg-red-500 hover:bg-red-600 text-white rounded-xl text-xs font-bold 
                                                shadow-md transition-all"
                                        >
                                            <Trash2 size={12} />
                                            <span>Remove</span>
                                        </button>
                                    </div>
                                ) : (
                                    <div className="space-y-2">
                                        {/* Generate transcript button */}
                                        <button
                                            onClick={handleStartTranscription}
                                            disabled={!canTranscribe || transcriptionState.isTranscribing}
                                            className="w-full flex items-center justify-center gap-1.5 px-3 py-2 
                                                bg-indigo-600 text-white rounded-xl text-xs font-bold 
                                                hover:bg-indigo-700 shadow-md shadow-indigo-200 transition-all
                                                disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            {transcriptionState.isTranscribing ? (
                                                <>
                                                    <Loader2 size={12} className="animate-spin" />
                                                    <span>Processing...</span>
                                                </>
                                            ) : (
                                                <>
                                                    <Sparkles size={12} />
                                                    <span>Generate Transcript</span>
                                                </>
                                            )}
                                        </button>
                                        {/* Upload transcript */}
                                        <button
                                            onClick={() => transcriptInputRef.current?.click()}
                                            disabled={transcriptionState.isUploadingTranscript}
                                            className="w-full flex items-center justify-center gap-1.5 px-3 py-2 
                                                bg-slate-100 text-slate-600 rounded-xl text-xs font-bold 
                                                hover:bg-slate-200 transition-all
                                                disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            {transcriptionState.isUploadingTranscript ? (
                                                <>
                                                    <Loader2 size={12} className="animate-spin" />
                                                    <span>Uploading...</span>
                                                </>
                                            ) : (
                                                <>
                                                    <Upload size={12} />
                                                    <span>Upload Transcript</span>
                                                </>
                                            )}
                                        </button>
                                        <p className="text-[9px] text-slate-400 text-center">
                                            Supports .txt, .json, .srt, .vtt files
                                        </p>
                                    </div>
                                )}
                            </div>

                            {/* Audio Source */}
                            <div className={`bg-white border p-4 rounded-xl shadow-sm hover:shadow-md transition-all
                                ${(hasAudio || uploadedAudio) ? 'border-green-200 ring-1 ring-green-100' : 'border-slate-100'}`}>
                                <div className="flex items-center justify-between mb-3">
                                    <div className="flex items-center gap-3">
                                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center
                                            ${(hasAudio || uploadedAudio) ? 'bg-green-100' : 'bg-slate-100'}`}>
                                            <Music size={18} className={(hasAudio || uploadedAudio) ? 'text-green-600' : 'text-slate-500'} />
                                        </div>
                                        <div>
                                            <h4 className="text-sm font-bold text-slate-800">Audio</h4>
                                            <p className="text-[10px] text-slate-400">
                                                {(hasAudio || uploadedAudio) ? (uploadedAudio?.fileName || 'Uploaded') : 'MP3, WAV, M4A'}
                                            </p>
                                        </div>
                                    </div>
                                    {(hasAudio || uploadedAudio) && (
                                        <div className="w-5 h-5 bg-green-500 rounded-full flex items-center justify-center">
                                            <CheckCircle size={12} className="text-white" />
                                        </div>
                                    )}
                                </div>

                                {/* Audio Upload Progress - Inline like video */}
                                <AnimatePresence>
                                    {isUploadingAudio && (
                                        <motion.div
                                            initial={{ opacity: 0, height: 0 }}
                                            animate={{ opacity: 1, height: 'auto' }}
                                            exit={{ opacity: 0, height: 0 }}
                                            className="mb-3"
                                        >
                                            <div className="bg-indigo-50 rounded-xl p-3 border border-indigo-100">
                                                <div className="flex items-center justify-between gap-2">
                                                    <div className="flex items-center gap-2 flex-1 min-w-0">
                                                        {isCancellingAudio ? (
                                                            <X size={16} className="text-indigo-600" />
                                                        ) : (
                                                            <Loader2 size={16} className="text-indigo-600 animate-spin" />
                                                        )}
                                                        <span className="text-xs font-bold text-indigo-900">
                                                            {isCancellingAudio ? 'Cancelling...' : 'Uploading...'}
                                                        </span>
                                                    </div>
                                                    {/* Cancel Button */}
                                                    {!isCancellingAudio && (
                                                        <motion.button
                                                            whileHover={{ scale: 1.05 }}
                                                            whileTap={{ scale: 0.95 }}
                                                            onClick={handleCancelAudioUpload}
                                                            className="flex items-center gap-1 px-2.5 py-1.5 bg-red-500 hover:bg-red-600 
                                                                text-white rounded-lg text-[10px] font-bold transition-colors flex-shrink-0 shadow-md"
                                                        >
                                                            <Square size={10} />
                                                            Cancel
                                                        </motion.button>
                                                    )}
                                                </div>
                                                <div className="mt-2 h-1.5 bg-indigo-200 rounded-full overflow-hidden">
                                                    <motion.div
                                                        className="h-full bg-indigo-600 rounded-full"
                                                        initial={{ width: 0 }}
                                                        animate={{ width: `${audioUploadProgress}%` }}
                                                    />
                                                </div>
                                                <p className="text-[10px] text-indigo-600 mt-1">{audioUploadProgress}%</p>
                                            </div>
                                        </motion.div>
                                    )}
                                </AnimatePresence>

                                {/* Audio Error Message - Inline */}
                                <AnimatePresence>
                                    {audioUploadError && (
                                        <motion.div
                                            initial={{ opacity: 0, height: 0 }}
                                            animate={{ opacity: 1, height: 'auto' }}
                                            exit={{ opacity: 0, height: 0 }}
                                            className="mb-3"
                                        >
                                            <div className="bg-red-50 rounded-xl p-3 border border-red-200">
                                                <div className="flex items-start gap-2">
                                                    <AlertCircle size={14} className="text-red-600 mt-0.5" />
                                                    <div className="flex-1">
                                                        <span className="text-xs font-bold text-red-900">Upload failed</span>
                                                        <p className="text-[10px] text-red-700 mt-0.5">{audioUploadError}</p>
                                                    </div>
                                                    <button
                                                        onClick={() => setAudioUploadError(null)}
                                                        className="text-red-600 hover:text-red-800"
                                                    >
                                                        <X size={14} />
                                                    </button>
                                                </div>
                                            </div>
                                        </motion.div>
                                    )}
                                </AnimatePresence>

                                {/* Audio Preview Player - Show after successful upload */}
                                <AnimatePresence>
                                    {(hasAudio || uploadedAudio) && audioPreviewUrl && !isUploadingAudio && (
                                        <motion.div
                                            initial={{ opacity: 0, height: 0 }}
                                            animate={{ opacity: 1, height: 'auto' }}
                                            exit={{ opacity: 0, height: 0 }}
                                            className="mb-3"
                                        >
                                            <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl p-3 border border-indigo-100">
                                                <div className="flex items-center gap-2">
                                                    <button
                                                        onClick={handleAudioPlayPause}
                                                        className="w-8 h-8 rounded-full bg-indigo-600 hover:bg-indigo-700 
                                                            flex items-center justify-center transition-colors shadow-md flex-shrink-0"
                                                    >
                                                        {isPlayingAudio ? (
                                                            <Pause size={14} className="text-white" />
                                                        ) : (
                                                            <Play size={14} className="text-white ml-0.5" />
                                                        )}
                                                    </button>
                                                    <div className="flex-1 min-w-0">
                                                        <div className="flex items-center gap-2">
                                                            <span className="text-[10px] font-medium text-slate-600 w-8">
                                                                {formatAudioTime(audioCurrentTime)}
                                                            </span>
                                                            <input
                                                                type="range"
                                                                min="0"
                                                                max="100"
                                                                value={audioDuration ? (audioCurrentTime / audioDuration) * 100 : 0}
                                                                onChange={handleAudioSeek}
                                                                className="flex-1 h-1 bg-indigo-200 rounded-full appearance-none cursor-pointer
                                                                    [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-2.5 
                                                                    [&::-webkit-slider-thumb]:h-2.5 [&::-webkit-slider-thumb]:rounded-full 
                                                                    [&::-webkit-slider-thumb]:bg-indigo-600"
                                                            />
                                                            <span className="text-[10px] font-medium text-slate-600 w-8">
                                                                {formatAudioTime(audioDuration)}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                                <audio
                                                    ref={audioPlayerRef}
                                                    src={audioPreviewUrl}
                                                    onTimeUpdate={handleAudioTimeUpdate}
                                                    onLoadedMetadata={handleAudioLoadedMetadata}
                                                    onEnded={() => setIsPlayingAudio(false)}
                                                    onPlay={() => setIsPlayingAudio(true)}
                                                    onPause={() => setIsPlayingAudio(false)}
                                                    className="hidden"
                                                />
                                            </div>
                                        </motion.div>
                                    )}
                                </AnimatePresence>

                                {/* Action Buttons */}
                                {(hasAudio || uploadedAudio) ? (
                                    <button
                                        onClick={handleRemoveAudio}
                                        className="w-full flex items-center justify-center gap-1.5 px-3 py-2 
                                            bg-red-500 hover:bg-red-600 text-white rounded-xl text-xs font-bold 
                                            shadow-md transition-all"
                                    >
                                        <Trash2 size={12} />
                                        <span>Remove</span>
                                    </button>
                                ) : (
                                    <button
                                        onClick={() => audioInputRef.current?.click()}
                                        disabled={isUploadingAudio}
                                        className="w-full flex items-center justify-center gap-1.5 px-3 py-2 
                                            bg-indigo-600 text-white rounded-xl text-xs font-bold 
                                            hover:bg-indigo-700 shadow-md shadow-indigo-200 transition-all
                                            disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        <Upload size={12} />
                                        <span>Upload</span>
                                    </button>
                                )}
                            </div>

                            {/* Quick Tips */}
                            <div className="mt-4 p-3 bg-indigo-50/50 rounded-xl border border-indigo-100">
                                <h4 className="text-xs font-bold text-indigo-900 mb-1">💡 Quick Tip</h4>
                                <p className="text-[10px] text-indigo-700 leading-relaxed">
                                    Upload a video or paste a YouTube URL to unlock AI-powered content generation.
                                </p>
                            </div>

                            <p className="text-[10px] text-slate-400 text-center">
                                Max file size: {MAX_FILE_SIZE_GB}GB
                            </p>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* YouTube URL Input Modal - Rendered via Portal */}
            {createPortal(
                <AnimatePresence>
                    {showUrlInput && (
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm"
                            onClick={() => !isSavingUrl && setShowUrlInput(false)}
                        >
                            <motion.div
                                initial={{ scale: 0.95, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                exit={{ scale: 0.95, opacity: 0 }}
                                className="bg-white rounded-[32px] p-6 max-w-md w-full shadow-2xl"
                                onClick={(e) => e.stopPropagation()}
                            >
                                <div className="flex items-center justify-between mb-4">
                                    <h3 className="text-lg font-bold text-slate-900 font-['Outfit']">
                                        Add YouTube URL
                                    </h3>
                                    <button
                                        onClick={() => setShowUrlInput(false)}
                                        className="text-slate-400 hover:text-slate-600"
                                        disabled={isSavingUrl}
                                    >
                                        <X size={20} />
                                    </button>
                                </div>

                                <p className="text-sm text-slate-600 mb-4">
                                    Paste a YouTube video URL for transcript generation and content repurposing.
                                </p>

                                <div className="relative">
                                    <input
                                        type="url"
                                        value={youtubeUrl}
                                        onChange={(e) => {
                                            setYoutubeUrl(e.target.value);
                                            setUrlError(null);
                                        }}
                                        placeholder="https://youtube.com/watch?v=..."
                                        className="w-full px-4 py-3 rounded-xl border-2 border-slate-200 
                                            focus:border-indigo-500 focus:ring-0 outline-none
                                            text-sm text-slate-900 placeholder-slate-400
                                            transition-colors"
                                        disabled={isSavingUrl}
                                        autoFocus
                                    />
                                    {urlError && (
                                        <p className="text-xs text-red-600 mt-2">{urlError}</p>
                                    )}
                                </div>

                                <div className="flex gap-3 mt-6">
                                    <button
                                        onClick={() => setShowUrlInput(false)}
                                        className="flex-1 py-3 bg-slate-100 hover:bg-slate-200 text-slate-700 
                                            rounded-xl font-bold text-sm transition-colors"
                                        disabled={isSavingUrl}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={handleUrlSubmit}
                                        className="flex-1 py-3 bg-indigo-600 hover:bg-indigo-700 text-white 
                                            rounded-xl font-bold text-sm transition-colors
                                            flex items-center justify-center gap-2
                                            disabled:opacity-50 disabled:cursor-not-allowed"
                                        disabled={isSavingUrl || !youtubeUrl.trim()}
                                    >
                                        {isSavingUrl ? (
                                            <>
                                                <Loader2 size={16} className="animate-spin" />
                                                Saving...
                                            </>
                                        ) : (
                                            <>
                                                <Link size={16} />
                                                Save URL
                                            </>
                                        )}
                                    </button>
                                </div>
                            </motion.div>
                        </motion.div>
                    )}
                </AnimatePresence>,
                document.body
            )}

            {/* Source Selection Modal for Transcription - Rendered via Portal */}
            {createPortal(
                <AnimatePresence>
                    {showSourceSelectionModal && (
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm"
                            onClick={() => setShowSourceSelectionModal(false)}
                        >
                            <motion.div
                                initial={{ scale: 0.95, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                exit={{ scale: 0.95, opacity: 0 }}
                                className="bg-white rounded-[32px] p-6 max-w-md w-full shadow-2xl"
                                onClick={(e) => e.stopPropagation()}
                            >
                                <div className="flex items-center justify-between mb-4">
                                    <h3 className="text-lg font-bold text-slate-900 font-['Outfit']">
                                        Select Transcription Source
                                    </h3>
                                    <button
                                        onClick={() => setShowSourceSelectionModal(false)}
                                        className="text-slate-400 hover:text-slate-600"
                                    >
                                        <X size={20} />
                                    </button>
                                </div>

                                <p className="text-sm text-slate-600 mb-6">
                                    Both video and audio files are available. Which source would you like to transcribe from?
                                </p>

                                <div className="space-y-3">
                                    {/* Video Option */}
                                    <motion.button
                                        whileHover={{ scale: 1.02 }}
                                        whileTap={{ scale: 0.98 }}
                                        onClick={() => handleSourceSelection('video')}
                                        className="w-full p-4 rounded-2xl border-2 border-slate-200 hover:border-indigo-400 
                                            hover:bg-indigo-50 transition-all flex items-center gap-4 group"
                                    >
                                        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 
                                            flex items-center justify-center shadow-lg group-hover:shadow-indigo-200 transition-shadow">
                                            <Video size={22} className="text-white" />
                                        </div>
                                        <div className="flex-1 text-left">
                                            <h4 className="font-bold text-slate-900">Video Source</h4>
                                            <p className="text-xs text-slate-500 mt-0.5">
                                                {savedUrl?.url
                                                    ? 'YouTube URL'
                                                    : (uploadedAsset?.fileName || 'Uploaded video')}
                                            </p>
                                        </div>
                                        <ChevronRight size={18} className="text-slate-400 group-hover:text-indigo-500 transition-colors" />
                                    </motion.button>

                                    {/* Audio Option */}
                                    <motion.button
                                        whileHover={{ scale: 1.02 }}
                                        whileTap={{ scale: 0.98 }}
                                        onClick={() => handleSourceSelection('audio')}
                                        className="w-full p-4 rounded-2xl border-2 border-slate-200 hover:border-indigo-400 
                                            hover:bg-indigo-50 transition-all flex items-center gap-4 group"
                                    >
                                        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-green-500 to-teal-600 
                                            flex items-center justify-center shadow-lg group-hover:shadow-green-200 transition-shadow">
                                            <Music size={22} className="text-white" />
                                        </div>
                                        <div className="flex-1 text-left">
                                            <h4 className="font-bold text-slate-900">Audio Source</h4>
                                            <p className="text-xs text-slate-500 mt-0.5">
                                                {uploadedAudio?.fileName || 'Uploaded audio'}
                                            </p>
                                        </div>
                                        <ChevronRight size={18} className="text-slate-400 group-hover:text-indigo-500 transition-colors" />
                                    </motion.button>
                                </div>

                                <div className="mt-6 pt-4 border-t border-slate-100">
                                    <p className="text-[10px] text-slate-400 text-center">
                                        Audio files may transcribe faster as they skip the audio extraction step
                                    </p>
                                </div>
                            </motion.div>
                        </motion.div>
                    )}
                </AnimatePresence>,
                document.body
            )}

            {/* Transcription Progress Overlay */}
            <TranscriptionProgressOverlay
                isOpen={transcriptionState.isTranscribing}
                progress={transcriptionState.transcriptionProgress}
                stage={transcriptionState.transcriptionStage}
                message={transcriptionState.transcriptionMessage}
                startTime={transcriptionState.transcriptionStartTime}
                onCancel={handleCancelTranscription}
            />

            {/* Transcription Result Modal */}
            <TranscriptionResultModal
                isOpen={transcriptionState.showResultModal}
                onClose={() => dispatch({ type: 'HIDE_RESULT_MODAL' })}
                transcript={transcriptionState.transcript}
                episode={episode}
                user={user}
                onSaveToVault={(asset) => {
                    showToast('Transcript saved to vault!', 'success');
                }}
            />
        </div>
    );
};

export default RepurposeSidebar;
